import { Alert, Stack, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { V2ThemeProvider } from '@onevision-resources/design-theme';
import { portalPaths, UrlParams } from '@provision/portal-routes';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { applyNodeChanges, type NodeChange, type NodeMouseHandler } from 'reactflow';
import { Loader, NamedLayoutBlock } from 'src/components';
import {
  getWorkflowTriggerType,
  usePrefetchWorkflowAssignments,
  useWorkflowStepTypes,
  useWorkflowVersionMutations,
  useWorkflowVersions,
} from 'src/graphql/entities/workflow';
import type { WorkflowTriggerType } from 'src/graphql/entities/workflow/workflow.trigger';
import { useBeforeUnloadWarning } from 'src/pages/Settings/memberships/hooks/use-before-unload-warning';
import {
  WorkflowSelectionToolbar,
  WorkflowVersionActionsMenu,
} from 'src/pages/Settings/workflows/components';
import { useWorkflowCanvasView } from 'src/pages/Settings/workflows/use-workflow-canvas-view';
import {
  notSavedMessage,
  useWorkflowDraftEditor,
} from 'src/pages/Settings/workflows/use-workflow-draft-editor';
import { useWorkflowRouteRedirects } from 'src/pages/Settings/workflows/use-workflow-route-redirects';
import { useWorkflowsWithPlatform } from 'src/pages/Settings/workflows/use-workflows-with-platform';
import type { WorkflowDefinition } from 'src/pages/Settings/workflows/utils/workflow-definition';
import {
  buildReactFlowGraph,
  getWorkflowGraphStructureKey,
  parseWorkflowDefinition,
  type WorkflowGraphEdge,
  type WorkflowGraphNode,
  WorkflowLayoutDirection,
} from 'src/pages/Settings/workflows/utils/workflow-helpers';
import {
  type WorkflowEditorContextValue,
  WorkflowEditorProvider,
} from 'src/pages/Settings/workflows/workflow-editor-context';
import {
  DefinitionContent,
  VersionEmptyState,
  WorkflowDetailCard,
  WorkflowDetailContentContainer,
  WorkflowDetailsHeader,
  WorkflowEmptyState,
  WorkflowSelectionEmptyState,
} from 'src/pages/Settings/workflows/workflows-page.elements';
import { useGlobalStore } from 'src/store';
import { Modals, useModalMethods } from 'src/store/modals';
import { useWorkflowsStore } from 'src/store/workflows';
import { useConfirmationModal } from 'src/utils/hooks';
import { useNotifications } from 'src/utils/hooks/use-notifications';
import { useNavigateRoute } from 'src/utils/routing/use-navigate-route';

type RouteParamName<T extends string> = T extends `:${infer Name}` ? Name : never;

type WorkflowRouteParams = Partial<
  Record<
    RouteParamName<typeof UrlParams.workflowId | typeof UrlParams.workflowVersionId>,
    string
  >
>;

dayjs.extend(relativeTime);

export const WorkflowsPage = () => {
  const theme = useTheme();
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { showModal: showWorkflowAssignmentsModal } = useModalMethods(
    Modals.WorkflowAssignments,
  );
  const { showModal: showNewWorkflowModal } = useModalMethods(Modals.NewWorkflow);
  const { workflowId: workflowIdParam, workflowVersionId: workflowVersionIdParam } =
    useParams<WorkflowRouteParams>();
  const selectedWorkflowId = workflowIdParam || undefined;
  const selectedVersionId = workflowVersionIdParam || undefined;
  const [showRawDefinition, setShowRawDefinition] = useState(false);
  const [diagramNodes, setDiagramNodes] = useState<WorkflowGraphNode[]>([]);
  const [diagramEdges, setDiagramEdges] = useState<WorkflowGraphEdge[]>([]);
  const [diagramLoading, setDiagramLoading] = useState(false);
  /** The version the laid-out diagram belongs to; a re-layout of the same one keeps it on screen. */
  const [diagramVersionId, setDiagramVersionId] = useState<string | undefined>();
  const [layoutDirection, setLayoutDirection] = useState<WorkflowLayoutDirection>(
    WorkflowLayoutDirection.Down,
  );
  const { navigate } = useNavigateRoute();
  const setLastVisited = useWorkflowsStore.use.setLastVisited();
  const { confirm } = useConfirmationModal();
  const { showInfoAlert, showErrorAlert } = useNotifications();
  const { catalog, loading: stepTypesLoading, error: stepTypesError } = useWorkflowStepTypes();

  const {
    workflows: listedWorkflows,
    loading: workflowsLoading,
    refetchWorkflows,
  } = useWorkflowsWithPlatform(selectedPartnerId);

  // Partner 0's workflows are its own, so `isPlatform` is only ever set for another partner. A
  // workflow just created is the partner's own, so the listed rows are enough to decide this.
  const isViewOnly = listedWorkflows.some(
    (workflow) => workflow.workflowId === selectedWorkflowId && workflow.isPlatform,
  );

  // Warms the assignments modal's queries so it opens populated rather than empty. Only the
  // open modal is mounted, so it cannot do this for itself.
  usePrefetchWorkflowAssignments(selectedPartnerId);

  const {
    workflowVersions: listedWorkflowVersions,
    loading: versionsLoading,
    error: versionsError,
    refetchWorkflowVersions,
  } = useWorkflowVersions({
    workflowId: selectedWorkflowId,
  });

  const navigateToWorkflow = useCallback(
    (workflowId?: string, versionId?: string, options?: { replace?: boolean }) => {
      navigate(portalPaths.settings.workflows(workflowId, versionId), options);
    },
    [navigate],
  );

  // The lists as the page shows them: what was read, plus a just-created row the replica-backed
  // reads have not returned yet. See useWorkflowRouteRedirects.
  const { workflows, workflowVersions, holdSelection, releaseSelection, hideDeletedVersion } =
    useWorkflowRouteRedirects({
      selectedWorkflowId,
      selectedVersionId,
      workflows: listedWorkflows,
      workflowsLoading,
      workflowVersions: listedWorkflowVersions,
      versionsLoading,
      navigateToWorkflow,
      refetchWorkflows,
      refetchWorkflowVersions,
    });

  const selectedWorkflow = useMemo(
    () => workflows.find((workflow) => workflow.workflowId === selectedWorkflowId),
    [workflows, selectedWorkflowId],
  );
  const ownWorkflows = useMemo(
    () => workflows.filter((workflow) => !workflow.isPlatform),
    [workflows],
  );

  useEffect(() => {
    if (selectedWorkflowId && selectedVersionId) {
      setLastVisited(selectedWorkflowId, selectedVersionId);
    }
  }, [selectedWorkflowId, selectedVersionId, setLastVisited]);

  const selectedVersion = useMemo(
    () =>
      workflowVersions.find(
        (workflowVersion) => workflowVersion.workflowVersionId === selectedVersionId,
      ),
    [workflowVersions, selectedVersionId],
  );

  const parsedDefinition = useMemo(
    () => parseWorkflowDefinition(selectedVersion?.definition),
    [selectedVersion?.definition],
  );
  const definitionError = parsedDefinition.error;

  // A draft of the partner's own workflow, once the catalog has said what each step is.
  const editable =
    !!selectedVersion &&
    !selectedVersion.isActive &&
    !isViewOnly &&
    !stepTypesLoading &&
    !stepTypesError;

  const draftEditor = useWorkflowDraftEditor({
    workflowVersion: selectedVersion,
    storedDefinition: parsedDefinition.definition,
    catalog,
    editable,
    partnerId: selectedPartnerId,
  });
  const { isDirty, setOpenNodeId } = draftEditor;
  // reloading, closing the tab or leaving the site would drop unsaved draft edits
  useBeforeUnloadWarning(isDirty);
  const canvasView = useWorkflowCanvasView({
    versionId: selectedVersionId,
    definition: draftEditor.definition,
    catalog,
  });
  const { folded } = canvasView;

  /** Unsaved edits go when the page moves to another version; ask first. */
  const confirmLeavingDraft = useCallback(
    (): Promise<boolean> =>
      isDirty
        ? confirm({
            title: 'Discard unsaved changes?',
            body: 'This draft has changes that haven’t been saved.',
            confirmLabel: 'Discard changes',
            danger: true,
          })
        : Promise.resolve(true),
    [confirm, isDirty],
  );

  const handleWorkflowSelection = useCallback(
    async (workflowId?: string) => {
      if (!(await confirmLeavingDraft())) return;
      releaseSelection();
      navigateToWorkflow(workflowId, undefined);
    },
    [confirmLeavingDraft, navigateToWorkflow, releaseSelection],
  );

  const handleVersionSelection = useCallback(
    async (versionId?: string) => {
      if (!(await confirmLeavingDraft())) return;
      releaseSelection();
      if (!selectedWorkflowId) {
        navigateToWorkflow(undefined, undefined);
        return;
      }

      navigateToWorkflow(selectedWorkflowId, versionId);
    },
    [confirmLeavingDraft, navigateToWorkflow, releaseSelection, selectedWorkflowId],
  );

  const { createDraftWorkflowVersion, loading: versionMutationLoading } =
    useWorkflowVersionMutations({ partnerId: selectedPartnerId, refetchWorkflowVersions });

  // New › Draft version, and Edit when there's no newer draft, copy the selected version into a new
  // draft and open it on the canvas. The draft is then changed there, not by pasting JSON.
  const handleCreateDraft = useCallback(async () => {
    if (isViewOnly || !selectedWorkflowId || !selectedVersion?.definition) return;
    if (!(await confirmLeavingDraft())) return;

    const description = `Draft from version ${selectedVersion.version}`;
    const created = await createDraftWorkflowVersion({
      workflowId: selectedWorkflowId,
      definition: selectedVersion.definition,
      description,
    });
    if (!created) return;

    // The create hands back ids only, and the version list reads a replica that lags the write, so
    // the page holds the new draft's row itself — a copy of the version it was made from — and
    // opens it on the canvas at once instead of waiting for the list to catch up.
    const now = new Date().toISOString();
    holdSelection({
      workflowVersion: {
        ...selectedVersion,
        id: created.workflowVersionId,
        workflowVersionId: created.workflowVersionId,
        workflowId: created.workflowId,
        version: created.version,
        isActive: false,
        description,
        createdAt: now,
        updatedAt: now,
      },
    });
    navigateToWorkflow(created.workflowId, created.workflowVersionId);
  }, [
    confirmLeavingDraft,
    createDraftWorkflowVersion,
    holdSelection,
    isViewOnly,
    navigateToWorkflow,
    selectedVersion,
    selectedWorkflowId,
  ]);

  // Edit reopens the newest version when it's a draft — the one an earlier Edit made — instead of
  // copying the active version again; only when there's no newer draft does it start one.
  const newestVersion = useMemo(
    () =>
      workflowVersions.reduce<(typeof workflowVersions)[number] | undefined>(
        (newest, workflowVersion) =>
          !newest || workflowVersion.version > newest.version ? workflowVersion : newest,
        undefined,
      ),
    [workflowVersions],
  );
  const handleEdit = useCallback(async () => {
    if (newestVersion && !newestVersion.isActive) {
      await handleVersionSelection(newestVersion.workflowVersionId);
      return;
    }
    await handleCreateDraft();
  }, [handleCreateDraft, handleVersionSelection, newestVersion]);

  const openNewWorkflow = useCallback(
    async (prefill: {
      initialTriggerType?: WorkflowTriggerType;
      initialSourceWorkflowId?: string;
    }) => {
      // creating one opens it, which would leave this draft's unsaved edits behind
      if (!(await confirmLeavingDraft())) return;
      showNewWorkflowModal({
        ...prefill,
        // The rows come back with the create, so the page shows them at once rather than
        // waiting on lists that read a lagging replica. See useWorkflowRouteRedirects.
        onCreated: (result) => {
          holdSelection({
            workflow: { ...result.workflow, isPlatform: false },
            workflowVersion: result.workflowVersion,
          });
          navigateToWorkflow(result.workflowId, result.workflowVersionId);
        },
      });
    },
    [confirmLeavingDraft, holdSelection, navigateToWorkflow, showNewWorkflowModal],
  );

  const activeVersion = useMemo(
    () => workflowVersions.find((workflowVersion) => workflowVersion.isActive),
    [workflowVersions],
  );
  const activeTriggerType = useMemo(
    () => getWorkflowTriggerType(activeVersion?.definition),
    [activeVersion?.definition],
  );

  const handleCopyToNewWorkflow = useCallback(() => {
    if (!selectedWorkflowId || !activeTriggerType) {
      return;
    }

    void openNewWorkflow({
      initialTriggerType: activeTriggerType,
      initialSourceWorkflowId: selectedWorkflowId,
    });
  }, [activeTriggerType, openNewWorkflow, selectedWorkflowId]);

  // The layout follows the graph's shape, not its field values, so typing in a step doesn't move
  // the canvas. The key parses back into the definition the graph is built from.
  const graphStructureKey = draftEditor.definition
    ? getWorkflowGraphStructureKey(draftEditor.definition)
    : null;
  const graphStructure = useMemo(
    () => (graphStructureKey ? (JSON.parse(graphStructureKey) as WorkflowDefinition) : null),
    [graphStructureKey],
  );

  useEffect(() => {
    let isActive = true;

    const definition = graphStructure;
    if (!definition || stepTypesLoading) {
      setDiagramNodes([]);
      setDiagramEdges([]);
      setDiagramLoading(false);
      return;
    }

    setDiagramLoading(true);
    const runLayout = async () => {
      try {
        const graph = await buildReactFlowGraph(definition, {
          catalog,
          isDarkMode: theme.palette.mode === 'dark',
          layoutDirection,
          editable,
          folded,
        });
        if (!isActive) {
          return;
        }

        // a new layout keeps what was selected, so folding or adding a step doesn't drop it
        setDiagramNodes((previous) => {
          const selected = new Set(
            previous.filter((node) => node.selected).map(({ id }) => id),
          );
          return selected.size
            ? graph.nodes.map((node) =>
                selected.has(node.id) ? { ...node, selected: true } : node,
              )
            : graph.nodes;
        });
        setDiagramEdges(graph.edges);
        setDiagramVersionId(selectedVersionId);
      } catch (error) {
        console.error('Failed to build workflow graph', error);
        if (isActive) {
          setDiagramNodes([]);
          setDiagramEdges([]);
        }
      } finally {
        if (isActive) {
          setDiagramLoading(false);
        }
      }
    };

    void runLayout();

    return () => {
      isActive = false;
    };
  }, [
    catalog,
    editable,
    folded,
    graphStructure,
    layoutDirection,
    selectedVersionId,
    stepTypesLoading,
    theme.palette.mode,
  ]);

  // A draft with unsaved edits shows them in the JSON view too.
  const formattedDefinitionText = useMemo(() => {
    if (isDirty && draftEditor.definition) {
      return JSON.stringify(draftEditor.definition, null, 2);
    }
    if (!selectedVersion?.definition) {
      return 'No definition available.';
    }

    try {
      return JSON.stringify(JSON.parse(selectedVersion.definition), null, 2);
    } catch {
      return selectedVersion.definition;
    }
  }, [draftEditor.definition, isDirty, selectedVersion?.definition]);

  // "4 steps", or "2 of 4 steps shown" while outputs are folded; the trigger isn't a step here
  const stepCount = (draftEditor.definition?.nodes.length ?? 1) - 1;
  const shownStepCount = Math.max(canvasView.shownStepIds.size - 1, 0);
  const stepsLabel = folded.size
    ? `${shownStepCount} of ${stepCount} steps shown`
    : `${stepCount} ${stepCount === 1 ? 'step' : 'steps'}`;

  const noWorkflows = !workflowsLoading && workflows.length === 0;
  const noVersions =
    !versionsLoading && selectedWorkflowId && workflowVersions.length === 0 && !versionsError;
  const initialLoading = workflowsLoading && workflows.length === 0;

  // Disabled only while there is nothing to pick yet, not on `loading`: a background refetch sets
  // that too, and disabling a select for it closes its menu under the pointer.
  const workflowSelectDisabled = workflows.length === 0;
  const versionSelectDisabled = workflowSelectDisabled || workflowVersions.length === 0;

  const isShowingRawDefinition = showRawDefinition || !parsedDefinition.definition;
  const showDefinitionLoader = versionsLoading && !!selectedWorkflowId && !selectedVersion;
  // Only before a version's first layout. Re-laying out after an edit keeps the canvas mounted, so
  // the view isn't re-fitted (every card shrinking) and nothing on the page loses focus.
  const showDiagramLoader =
    (stepTypesLoading || (diagramLoading && diagramVersionId !== selectedVersionId)) &&
    !isShowingRawDefinition;
  const pendingDiagram =
    showDiagramLoader ||
    (!isShowingRawDefinition &&
      !diagramNodes.length &&
      !!parsedDefinition.definition &&
      !definitionError);
  const hasDiagramNodes = diagramNodes.length > 0;

  useEffect(() => {
    if (definitionError) {
      setShowRawDefinition(true);
      return;
    }

    setShowRawDefinition(false);
  }, [definitionError, selectedVersionId]);

  const handleDiagramNodeClick = useCallback<NodeMouseHandler>((_, node) => {
    setDiagramNodes((prev) => {
      const maxZ = prev.reduce((max, current) => Math.max(max, current.zIndex ?? 0), 0);
      const nextZ = maxZ + 1;
      return prev.map((item) =>
        item.id === node.id
          ? {
              ...item,
              zIndex: nextZ,
            }
          : item,
      );
    });
  }, []);

  // Steps leave the canvas only through the draft (Delete on the card), never as a canvas change.
  const handleNodesChange = useCallback((changes: NodeChange[]) => {
    setDiagramNodes((prev) =>
      applyNodeChanges(
        changes.filter(({ type }) => type !== 'remove'),
        prev,
      ),
    );
  }, []);

  const handleLayoutChange = useCallback((direction: WorkflowLayoutDirection) => {
    setLayoutDirection(direction);
  }, []);

  const handlePaneClick = useCallback(() => setOpenNodeId(null), [setOpenNodeId]);

  const { save: saveDraft } = draftEditor;
  const { discard: discardDraft } = draftEditor;
  // Discard is only enabled with unsaved changes, so it always asks first, as leaving does.
  const handleDiscardDraft = useCallback(async () => {
    if (!(await confirmLeavingDraft())) return;
    discardDraft();
    showInfoAlert('Changes discarded');
  }, [confirmLeavingDraft, discardDraft, showInfoAlert]);

  const { showStep } = canvasView;
  /** Opens a step and takes the canvas to it: unfolds what hides it, selects it, centres it. */
  const openStep = useCallback(
    (nodeId: string | null) => {
      if (nodeId) showStep(nodeId);
      setOpenNodeId(nodeId);
    },
    [setOpenNodeId, showStep],
  );

  const handleSaveDraft = useCallback(async () => {
    // What the canvas already knows the save would refuse: say what, and open that step
    const { clientProblems } = draftEditor;
    if (clientProblems.length && draftEditor.definition) {
      showErrorAlert(notSavedMessage(clientProblems, draftEditor.definition, catalog));
      openStep(clientProblems.find(({ nodeId }) => nodeId)?.nodeId ?? null);
      return;
    }
    const saved = await saveDraft();
    // A draft the list hasn't returned yet is shown from the held row, which the save would leave
    // stale; hold the saved one instead. A listed draft updates through the cache either way.
    if (saved) holdSelection({ workflowVersion: saved });
  }, [catalog, draftEditor, holdSelection, openStep, saveDraft, showErrorAlert]);

  const editorContextValue = useMemo<WorkflowEditorContextValue | null>(
    () =>
      draftEditor.definition
        ? {
            definition: draftEditor.definition,
            catalog,
            problemsByNode: draftEditor.problemsByNode,
            editable,
            openNodeId: draftEditor.openNodeId,
            setOpenNodeId,
            dispatch: draftEditor.dispatch,
            view: canvasView,
          }
        : null,
    [
      canvasView,
      catalog,
      draftEditor.definition,
      draftEditor.dispatch,
      draftEditor.openNodeId,
      draftEditor.problemsByNode,
      editable,
      setOpenNodeId,
    ],
  );

  const handleDeleteSuccess = useCallback(
    (deletedWorkflowVersionId: string) => {
      hideDeletedVersion(deletedWorkflowVersionId);
      releaseSelection();
      if (!selectedWorkflowId) {
        navigateToWorkflow(undefined, undefined, { replace: true });
        return;
      }

      navigateToWorkflow(selectedWorkflowId, undefined, { replace: true });
    },
    [hideDeletedVersion, navigateToWorkflow, releaseSelection, selectedWorkflowId],
  );

  const toolbarBorderColor =
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.12)
      : theme.palette.divider;

  const versionActions =
    selectedWorkflow && selectedVersion ? (
      <WorkflowVersionActionsMenu
        partnerId={selectedPartnerId}
        workflowName={selectedWorkflow.name}
        selectedVersion={selectedVersion}
        workflowVersions={workflowVersions}
        refetchWorkflowVersions={refetchWorkflowVersions}
        onDeleteSuccess={handleDeleteSuccess}
        viewOnly={isViewOnly}
        hasUnsavedChanges={isDirty}
      />
    ) : null;

  let primaryContent: ReactNode = null;

  if (initialLoading) {
    primaryContent = (
      <WorkflowDetailCard>
        <Stack alignItems="center" justifyContent="center" height="100%" minHeight={320}>
          <Loader />
        </Stack>
      </WorkflowDetailCard>
    );
  } else if (noWorkflows) {
    primaryContent = <WorkflowEmptyState />;
  } else if (noVersions) {
    primaryContent = (
      <WorkflowDetailCard>
        <VersionEmptyState workflowName={selectedWorkflow?.name} />
      </WorkflowDetailCard>
    );
  } else if (showDefinitionLoader) {
    primaryContent = (
      <WorkflowDetailCard>
        <Stack alignItems="center" justifyContent="center" height="100%" minHeight={320}>
          <Loader />
        </Stack>
      </WorkflowDetailCard>
    );
  } else if (selectedWorkflow && selectedVersion) {
    const definitionContent = (
      <DefinitionContent
        pendingDiagram={pendingDiagram}
        hasDiagramNodes={hasDiagramNodes}
        showRawDefinition={isShowingRawDefinition}
        diagramNodes={diagramNodes}
        diagramEdges={diagramEdges}
        layoutDirection={layoutDirection}
        definitionError={definitionError || undefined}
        formattedDefinitionText={formattedDefinitionText}
        onDiagramNodeClick={handleDiagramNodeClick}
        onNodesChange={handleNodesChange}
        onLayoutChange={handleLayoutChange}
        onPaneClick={handlePaneClick}
      />
    );

    primaryContent = (
      <WorkflowDetailCard>
        <WorkflowDetailsHeader
          workflowName={selectedWorkflow.name}
          versionNumber={selectedVersion.version}
          statusLabel={selectedVersion.isActive ? 'Active' : 'Draft'}
          stepsLabel={stepsLabel}
          updatedAtLabel={dayjs(selectedVersion.updatedAt).fromNow()}
          showRawDefinition={isShowingRawDefinition}
          onToggleDefinition={() => {
            if (definitionError || !parsedDefinition.definition) {
              return;
            }

            setShowRawDefinition((prev) => !prev);
          }}
          disableToggle={!!definitionError || !parsedDefinition.definition}
          viewOnly={isViewOnly}
          onCopyToNewWorkflow={handleCopyToNewWorkflow}
          copyToNewWorkflowDisabled={!activeTriggerType}
          onEdit={
            selectedVersion.isActive && !isViewOnly && !definitionError
              ? () => void handleEdit()
              : undefined
          }
          isStartingDraft={versionMutationLoading.isCreatingDraft}
          draft={
            editable
              ? {
                  isDirty,
                  isSaving: draftEditor.isSaving,
                  stepsNeedingAttention: draftEditor.stepsNeedingAttention,
                  onShowFirstProblem: () => openStep(draftEditor.firstProblemNodeId),
                  onDiscard: () => void handleDiscardDraft(),
                  onSave: () => void handleSaveDraft(),
                }
              : undefined
          }
        />
        {stepTypesError ? (
          <Alert severity="warning" sx={{ mx: 3, mt: 2 }}>
            Couldn’t load the step types, so this workflow can only be viewed.
          </Alert>
        ) : null}
        <WorkflowDetailContentContainer>
          {editorContextValue ? (
            <WorkflowEditorProvider value={editorContextValue}>
              {definitionContent}
            </WorkflowEditorProvider>
          ) : (
            definitionContent
          )}
        </WorkflowDetailContentContainer>
      </WorkflowDetailCard>
    );
  } else if (!versionsLoading) {
    primaryContent = (
      <WorkflowDetailCard>
        <WorkflowSelectionEmptyState />
      </WorkflowDetailCard>
    );
  }

  return (
    <NamedLayoutBlock
      title="Workflows"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
      BlockContentProps={{
        sx: { flex: 1, p: 0 },
      }}
    >
      <Stack spacing={0} sx={{ flex: 1, minHeight: 0 }}>
        <Stack
          spacing={1}
          sx={{
            pb: 2,
            borderBottom: `1px solid ${toolbarBorderColor}`,
            p: theme.spacing(2),
          }}
        >
          <V2ThemeProvider>
            <WorkflowSelectionToolbar
              workflows={workflows}
              workflowVersions={workflowVersions}
              selectedWorkflowId={selectedWorkflowId}
              selectedVersionId={selectedVersionId}
              workflowSelectDisabled={workflowSelectDisabled}
              versionSelectDisabled={versionSelectDisabled}
              newDraftVersionDisabled={isViewOnly || ownWorkflows.length === 0}
              onWorkflowChange={handleWorkflowSelection}
              onVersionChange={handleVersionSelection}
              onNewWorkflow={() => void openNewWorkflow({})}
              onNewDraftVersion={() => void handleCreateDraft()}
              onOpenAssignments={() => showWorkflowAssignmentsModal(null)}
              versionActions={versionActions}
            />
          </V2ThemeProvider>
        </Stack>

        <Stack sx={{ flex: 1, minHeight: 0, py: 2 }}>{primaryContent}</Stack>
      </Stack>
    </NamedLayoutBlock>
  );
};
